/**
 * Classifies a Cognito `Auth.forgotPassword` failure into "hide this from the user"
 * vs "show it".
 *
 * WHY THIS EXISTS — the reset flow has to satisfy two requirements that pull against
 * each other:
 *
 *  1. **Do not leak whether an account exists.** Anyone can type any address into the
 *     forgot-password box, so the response must look identical for a real account, a
 *     never-activated invite, a disabled account and an address we have never seen.
 *     `AppWithAuth.tsx` has swallowed `UserNotFoundException` for exactly this reason.
 *  2. **Do not strand the user.** A brand-new admin-created account (Cognito status
 *     `FORCE_CHANGE_PASSWORD`) *cannot* use forgot-password at all: Cognito refuses to
 *     reset a password that was never set, and sends no mail. Retrying — for hours, as
 *     actually happened — can never work. The way out is the temporary password from
 *     the admin, and nothing on that screen said so.
 *
 * The resolution: keep every user-state failure INDISTINGUISHABLE in the UI (this
 * module decides which ones), and put the escape hatch in copy shown to EVERYONE on the
 * confirm screen, so it guides the stranded user without telling an attacker anything.
 * The specific reason is reported to telemetry, which is not visible to the user.
 *
 * The reasons here are NOT shown to the user. They exist so an operator can tell, in
 * RUM, that resets are failing and why — this class of failure was previously invisible
 * on every layer.
 */

/** Why a reset produced no email. Telemetry only — never rendered. */
export type ForgotPasswordSuppressedReason =
  | 'user_not_found'
  | 'not_activated'
  | 'user_disabled'
  | 'no_verified_channel'
  | 'not_authorized';

export type ForgotPasswordClassification =
  | { suppress: true; reason: ForgotPasswordSuppressedReason }
  | { suppress: false };

/** Cognito errors arrive as `{ name, message }`; be defensive about both. */
function readError(error: unknown): { name: string; message: string } {
  if (typeof error !== 'object' || error === null) {
    return { name: '', message: typeof error === 'string' ? error : '' };
  }
  const candidate = error as { name?: unknown; message?: unknown };
  return {
    name: typeof candidate.name === 'string' ? candidate.name : '',
    message: typeof candidate.message === 'string' ? candidate.message : '',
  };
}

export function classifyForgotPasswordError(error: unknown): ForgotPasswordClassification {
  const { name, message } = readError(error);

  // The account does not exist. The original, and still the most important one.
  if (name === 'UserNotFoundException') {
    return { suppress: true, reason: 'user_not_found' };
  }

  // Every NotAuthorizedException on THIS call is a statement about the account's
  // state, so all of them are suppressed; only the telemetry reason differs.
  // The one that stranded a real user is "User password cannot be reset in the
  // current state" — a FORCE_CHANGE_PASSWORD account that has never been activated.
  if (name === 'NotAuthorizedException') {
    if (/current state/i.test(message)) {
      return { suppress: true, reason: 'not_activated' };
    }
    if (/disabled/i.test(message)) {
      return { suppress: true, reason: 'user_disabled' };
    }
    return { suppress: true, reason: 'not_authorized' };
  }

  // "Cannot reset password for the user as there is no registered/verified email or
  // phone_number" — also an existence oracle if surfaced, so it is suppressed too.
  // Other InvalidParameterExceptions are our own bad request and should surface.
  if (
    name === 'InvalidParameterException' &&
    /no registered\/verified|verified email/i.test(message)
  ) {
    return { suppress: true, reason: 'no_verified_channel' };
  }

  // Everything else is shown. `LimitExceededException` ("Attempt limit exceeded, please
  // try after some time") MUST reach the user: it is actionable, and it is rate-limiting
  // rather than an existence oracle — it fires the same way for an address with no
  // account. Network and configuration errors surface for the same reason.
  return { suppress: false };
}
